/**
 * The projection engine — what each cell of the progression matrix holds, and how a trainer pins one.
 *
 * A phase's exercise authors ONE base cycle per set; this module projects the rest for PREVIEW, and
 * the preview must equal what the backend will materialise. It does, and that is not a hope: every
 * vector of `__fixtures__/progression-projection-parity.json` is replayed through the same entry
 * points a screen calls (`progressionParity.test.ts`). Once the trainer touches a cell the preview
 * stops being cosmetic — a pinned value is PERSISTED as an override — so a drift here is data
 * corruption rather than a rendering bug.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/progressionMatrix.ts` ──────────
 * Logic identical, and the parity fixture is what proves it. The divergences are exhaustively:
 *
 * 1. **`isBackoffSet` and `derivesLoadInSession` come from `setShape.ts`**, this slice's own ladder
 *    module, rather than from a `shared/lib` shared with an in-gym logger this app does not have.
 *    Same predicates, same two fields.
 * 2. **`MatrixCell` carries `cycleOffset`.** Mobile recomputes it at the renderer; the KANON matrix
 *    needs it on every cell, and re-deriving it there would mean the renderer knowing the lifespan's
 *    origin — which is exactly the coordinate bug `cycleLifespan.ts` exists to prevent.
 * 3. **The BASE column is not editable, and `commitBackoffBasePercent` did not come across.** Here
 *    the authored column is never pinnable at any level (`setMatrixOverride` refuses a cycle at or
 *    before the base) because it IS the set's prescription, and editing it means editing the set
 *    table. The gap that encodes: a descent's base fraction is reachable only through the set
 *    table's percent cell, so if that surface ever stops offering the column for a ladder row, the
 *    fix is the set table's rather than a base-column editor here.
 * 4. **An override is a FLAT ROW of eleven prescribed columns**, not mobile's `values` map, because
 *    that is the shape `readOverrides` loads and `overrideWireInputs` emits. `overrideValueOn` and
 *    `withAxisPin` read and write ONE of them — the active axis's `setField` — which is what makes a
 *    pin per COLUMN rather than per cell.
 *
 * ── Coordinates ──────────────────────────────────────────────────────────────────────────────
 * `cycleIndex` is ABSOLUTE (1..Σ cycleCount), exactly as the wire's `PostSetOverrideInput` is.
 * `setIndex` is POSITIONAL — the set's 1-based place in the authored array, warm-ups included —
 * which is the same index the backend assigns from the array order, so an override key always lines
 * up with the materialised cell.
 *
 * The offset a cell projects at is its POSITION IN THE LIFESPAN, never `cycleIndex − baseCycle`:
 * progression counts EXPOSURES, so a cycle the exercise sits out advances nothing. That is why the
 * lifespan travels whole rather than as a start and a count.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { MeasurementFieldKey } from '~/shared/measurement';

import type { CycleLifespan } from './cycleLifespan';
import type { ExerciseDraft, ProgressionOverride, SetDraft } from './planDraft.types';
import {
  axisBaseOf,
  onPercentGrid,
  snapToAxis,
  snapToProgressionGrid,
  PERCENT_AXIS,
  type ProgressionAxis,
  type ProgressionAxisKind,
} from './progressionAxis';
import { ruleDelta, ruleDeltaUnit, ruleFor, type ProgressionRule } from './progressionRules';
import { countsAsWork } from './setCounting';
import { derivesLoadInSession, isBackoffSet, TOP_SET_TYPE, WORKING_SET_TYPE } from './setShape';

/**
 * How a rule grows a base per cycle. `additive` adds a constant per cycle — a kilogram step,
 * repetitions, seconds, effort points; `compound` grows geometrically; `flat` copies the base.
 */
export type ProjectionRule =
  | { readonly kind: 'flat' }
  | { readonly kind: 'additive'; readonly value: number }
  | { readonly kind: 'compound'; readonly value: number };

/**
 * The preview projection of ONE rule on the field it writes.
 *
 * A rule carrying a delta projects directly (`Percent` → compound, `Absolute` → additive); a flat
 * `Static` copies the base. A `PreviousExecution` delta is previewed the same way even though it
 * only pays out on a session the client delivered — the authored base is the honest reference
 * stand-in, and showing the step is what the trainer is authoring; nothing else is known at
 * authoring time. `progressionParity` pins that divergence by name rather than letting it drift.
 * On a live fork something else IS known, and `chainAnchorOf` restarts the preview from it.
 *
 * THE DELTA STATES ITS KIND AND THE COLUMN STATES ITS UNIT, so this does not ask which axis it is
 * on. On a row priced as a fraction of the max, an authored 2,5 is two and a half PERCENTAGE POINTS;
 * what the unit means is `prescribedPercentInCycle`'s to know, and it is the only caller that has to.
 */
export function projectionRuleFor(rule: ProgressionRule | null): ProjectionRule {
  const delta = ruleDelta(rule);
  if (rule !== null && delta !== null) {
    return ruleDeltaUnit(rule) === 'Percent'
      ? { kind: 'compound', value: delta }
      : { kind: 'additive', value: delta };
  }
  return { kind: 'flat' };
}

/**
 * The projection an exercise previews on the active axis — its rule for that axis's field, or flat
 * when it has none.
 *
 * THE MEASUREMENT CONTRACT IS PART OF THE ANSWER, and this is where it enters. A rule keyed on a
 * column the movement does not measure is not refused at save — the field a rule WRITES is the one
 * exception exercise-measurements R9 carves out, because every exercise carries a load rule by
 * default and that is how "this does not progress" is spelled. What such a rule does instead is
 * nothing: it resolves nothing and degrades silently. Previewing it as an advancing rule would be
 * the preview promising a progression the engine will not perform — which is exactly what the
 * backend's `FlatWeightDefault` normalisation produces on every unloaded movement.
 */
export function ruleFromExercise(
  exercise: Pick<ExerciseDraft, 'progressionRules' | 'measurementFields'>,
  axis: ProgressionAxis,
): ProjectionRule {
  // The %1RM axis reads the WEIGHT rule: the column holds the same prescription in another unit, so
  // the rule that prices load is the one that moves it.
  const field = axis.kind === 'percent' ? 'weight' : axis.field;
  if (field === null || !exercise.measurementFields.includes(field)) return { kind: 'flat' };
  return projectionRuleFor(ruleFor(exercise.progressionRules, field));
}

/**
 * Does the rule on this axis resolve against LIVE CLIENT DATA at workout start, rather than at
 * authoring time?
 *
 * `PreviousExecution` is the one family that does, so every cell past the base column is a stand-in:
 * the real number does not exist until the client's history is read. Callers that would PERSIST a
 * projected cell have to know the difference, or they store an estimate as a prescription.
 */
export function resolvesInSession(
  exercise: Pick<ExerciseDraft, 'progressionRules' | 'measurementFields'>,
  axis: ProgressionAxis,
): boolean {
  const field = axis.kind === 'percent' ? 'weight' : axis.field;
  if (field === null || !exercise.measurementFields.includes(field)) return false;
  return ruleFor(exercise.progressionRules, field)?.type === 'PreviousExecution';
}

/** Round to 2 decimals to shed compound-percent float noise while keeping .5 steps exact. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Project a base value `cycleOffset` exposures ahead (offset 0 is the base and comes back
 * untouched). A null base stays null; a flat rule copies the base. Additive and compound both
 * operate on the raw value in whatever unit the matrix is in.
 */
export function projectValue(
  base: number | null,
  cycleOffset: number,
  rule: ProjectionRule,
): number | null {
  if (base === null || cycleOffset <= 0) return base;
  if (rule.kind === 'flat') return base;
  if (rule.kind === 'additive') return round2(base + rule.value * cycleOffset);
  return round2(base * Math.pow(1 + rule.value / 100, cycleOffset));
}

/**
 * Advance one FIELD's authored base to a cycle offset and put the result on that field's progression
 * grid — the mirror of the backend's `ProgressionCalculator.Advance`, and the arithmetic the parity
 * fixture pins. Every field the dictionary gives a progression grid goes through it, not only the
 * columns a surface can show.
 */
export function advanceFieldValue(
  base: number | null,
  field: MeasurementFieldKey,
  rule: ProjectionRule,
  cycleOffset: number,
): number | null {
  // Offset 0 is the trainer's own authoring, not a machine derivation, so it is never put on a grid.
  if (base === null || cycleOffset <= 0) return base;
  const value = projectValue(base, cycleOffset, rule);
  return value === null ? null : snapToProgressionGrid(value, field);
}

/**
 * Advance a base on whichever AXIS it is written in — a field's progression grid, or the fraction of
 * the client's max. The %1RM axis has no field of its own: its delta is percentage points and its
 * grid is the stored fraction's, so stepping a 0,75 through the weight field's arithmetic added 2,5
 * to a fraction and snapped it to 1,25 kg, which the trajectory preview then drew as „375".
 */
export function advanceOnAxis(
  base: number | null,
  axis: ProgressionAxis,
  rule: ProjectionRule,
  cycleOffset: number,
): number | null {
  if (axis.field !== null) return advanceFieldValue(base, axis.field, rule, cycleOffset);
  if (base === null || cycleOffset <= 0) return base;
  return advanceLoadPercent(base, rule, cycleOffset);
}

/**
 * The FRACTION OF THE CLIENT'S MAX a row is priced at in a given cycle — the other unit a load can
 * be written in, and the mirror of the backend's `PrescriptionExpander.ProjectPercentAcrossCycles`.
 * Null where the row states no fraction at all: a plan written in kilograms is not a plan priced at
 * 0 % of anything.
 *
 * The policy is a hierarchy, and every branch ends on the column's grid because every branch ends up
 * SAVED:
 *
 * · a warm-up ramp rides its authored fraction flat (it is never progressed), and so does a LADDER
 *   member, whose percent is a fraction of the top set's actual rather than of the client's max —
 *   the rule that prices the load has no say in either;
 * · every other row expands through the exercise's WEIGHT rule, off its own fraction or, where it
 *   states none, off the exercise's multiplier.
 *
 * The weight rule and no other: the fraction is a price for the load, so a repetition or effort rule
 * has nothing to say about it, and letting each rule take a turn would compound one column by
 * several unrelated deltas.
 */
export function prescribedPercentInCycle(
  exercise: Pick<
    ExerciseDraft,
    'progressionRules' | 'measurementFields' | 'realMaxMultiplier'
  >,
  set: Pick<SetDraft, 'setType' | 'prescribedPercent' | 'prescribedWeight'>,
  cycleOffset: number,
): number | null {
  const authored = set.prescribedPercent;
  if (set.setType === 'Warmup' || derivesLoadInSession(set)) {
    return authored === null ? null : onPercentGrid(authored);
  }
  const rule = ruleFromExercise(exercise, PERCENT_AXIS);
  return advanceLoadPercent(authored ?? seedableMultiplier(exercise, set, rule), rule, cycleOffset);
}

/**
 * Whether the exercise's MULTIPLIER may stand in as this row's base fraction. Only where the row
 * prices itself in nothing: a row carrying kilograms advances on the kilogram axis, a row carrying
 * its own percent advances off that, and a percent-less AMRAP set must never be given one at all — a
 * percent is what tells a ladder member from an independent test, so writing one would silently move
 * the row into the ladder.
 *
 * A FLAT rule seeds nothing either. The multiplier already prices every unpriced row at workout
 * start; restating it per cycle for a projection that does not move would say the same thing twice,
 * and the backend, which MATERIALISES what this previews, refuses to rewrite existing plans that way.
 */
function seedableMultiplier(
  exercise: Pick<ExerciseDraft, 'realMaxMultiplier'>,
  set: Pick<SetDraft, 'setType' | 'prescribedWeight'>,
  rule: ProjectionRule,
): number | null {
  if (rule.kind === 'flat' || set.prescribedWeight !== null) return null;
  return set.setType === WORKING_SET_TYPE || set.setType === TOP_SET_TYPE
    ? exercise.realMaxMultiplier
    : null;
}

/**
 * Advance a fraction of the client's max — the mirror of `ProgressionCalculator.AdvancePercent`.
 *
 * THE DELTA'S KIND DECIDES HOW, WHILE THE COLUMN DECIDES THE UNIT: an Absolute delta is a number of
 * PERCENTAGE POINTS, so an authored 2,5 walks 0,80 → 0,825 → 0,85 in equal steps, and a Percent
 * delta compounds the fraction itself exactly as it compounds kilograms. Overflow plateaus at the
 * ends of the axis rather than refusing — a plan that runs off the top still expresses the delta the
 * trainer authored, and a flat tail at the whole max is a prescription the client can be given.
 *
 * Unlike the field axes, offset 0 is NOT returned verbatim: what arrives may be the exercise's
 * multiplier standing in for a fraction, which has a wider range of its own, so the base goes on the
 * grid like every other answer.
 */
function advanceLoadPercent(
  basePercent: number | null,
  rule: ProjectionRule,
  cycleOffset: number,
): number | null {
  if (basePercent === null) return null;
  if (rule.kind === 'flat' || cycleOffset <= 0) return onPercentGrid(basePercent);
  return onPercentGrid(
    rule.kind === 'compound'
      ? basePercent * Math.pow(1 + rule.value / 100, cycleOffset)
      : basePercent + (rule.value / 100) * cycleOffset,
  );
}

/**
 * Project a base value AND snap the result to the active axis's grid. Every MACHINE-computed cell on
 * a FIELD axis goes through this, so the displayed AND persisted value is always on-grid. The
 * authored base is never passed here, and neither is the fraction axis, whose unit and seed are
 * `prescribedPercentInCycle`'s.
 */
export function projectSnapped(
  base: number | null,
  cycleOffset: number,
  rule: ProjectionRule,
  axis: ProgressionAxis,
): number | null {
  const value = projectValue(base, cycleOffset, rule);
  if (value === null) return value;
  return snapToAxis(value, axis);
}

/**
 * How a cell was produced.
 *
 * `computed` and `flat` are both machine-produced later cycles, and ONE question tells them apart:
 * did a rule actually advance this column? `computed` means a rule moved the value and the cell
 * states a result. `flat` means nothing moved it, so the cell repeats the base — a column with no
 * rule, a rule with no delta, or a rule the movement's contract leaves unresolved. That distinction
 * is not cosmetic: every later cell used to be `computed`, so an exercise nobody had authored a
 * progression for painted its repeated base values in the same certain green as a real +2,5 kg
 * ladder, which is the screen asserting a calculation over numbers that were copied.
 *
 * `warmupFlat` says the same thing for a different reason (R6: a warm-up is never resolved by any
 * rule at any moment, whatever the column carries), which is why it stays a name of its own.
 */
export type MatrixCellKind = 'base' | 'computed' | 'flat' | 'override' | 'warmupFlat';

/** One resolved cell. `valueRaw` is in the active unit — kilograms, or the 0–1 percent fraction. */
export interface MatrixCell {
  readonly setIndex: number;
  readonly cycleIndex: number;
  /** Exposures past the exercise's own base. 0 is the authored column. */
  readonly cycleOffset: number;
  readonly valueRaw: number | null;
  readonly kind: MatrixCellKind;
}

/** One row — one authored set, across every cycle the exercise is prescribed in. */
export interface MatrixRow {
  readonly setIndex: number;
  readonly isWarmup: boolean;
  /**
   * A back-off DESCENT row. Its cells carry the "% of the top" fraction on EVERY axis rather than a
   * value on the active one, because the percent is the whole prescription: the load does not exist
   * until the client logs the top (R11), and the only per-cycle edit the backend accepts on such a
   * row is its percent.
   */
  readonly isBackoffDescent: boolean;
  readonly cells: readonly MatrixCell[];
}

/** The raw base a set contributes on the active axis. */
export function baseValueOf(set: SetDraft, axis: ProgressionAxis): number | null {
  return axisBaseOf(set, axis);
}

/**
 * Is this override a LADDER PERCENT — a "% of the top" pinned on one cycle of a ladder member?
 *
 * The one predicate that tells those apart from every other override, and the reason it needs the
 * sets: `prescribedPercent` is ALSO the %1RM axis's override field, so the field alone is ambiguous
 * and only the row it sits on says which of the two it is.
 *
 * It matters because a ladder percent is not a value on any progression axis. It is the descent's
 * entire prescription, authored cycle by cycle, and it stays valid whatever axis the exercise later
 * progresses on — so every wholesale rewrite of `progressionOverrides` has to carry these across or
 * it silently deletes authored work.
 */
export function isLadderPercentOverride(
  override: ProgressionOverride,
  sets: readonly SetDraft[],
): boolean {
  if (override.prescribedPercent === null) return false;
  const set = sets[override.setIndex - 1];
  return set !== undefined && derivesLoadInSession(set);
}

/** The ladder percents a rewrite must preserve. */
export function ladderPercentOverrides(
  overrides: readonly ProgressionOverride[],
  sets: readonly SetDraft[],
): ProgressionOverride[] {
  return overrides.filter((override) => isLadderPercentOverride(override, sets));
}

/**
 * The overrides that rewriting ONE COLUMN's rule would genuinely drop.
 *
 * A RULE IS KEYED BY THE COLUMN IT WRITES, so rewriting it can only disturb pins on that same
 * column. Asking without one — counting every non-ladder pin in the exercise — made a confirm report
 * values a change on the repetitions column could not touch, and made "recompute from the base"
 * delete the trainer's hand-typed kilograms as the price of retyping a rep rule.
 */
export function overridesDroppedByRewrite(
  overrides: readonly ProgressionOverride[],
  sets: readonly SetDraft[],
  axis: ProgressionAxis,
): ProgressionOverride[] {
  return overrides.filter(
    (override) =>
      !isLadderPercentOverride(override, sets) && overrideValueOn(override, axis) !== null,
  );
}

/**
 * Drop every pin on ONE column, leaving the same cells' pins on every other column standing.
 *
 * Per-cell rather than per-override, because one override holds a whole cell: a week the trainer
 * corrected the reps AND the load in carries both numbers, and recomputing the rep column has no
 * business touching the load. A cell left holding nothing is dropped by `upsertOverride` itself.
 */
export function clearColumnPins(
  overrides: readonly ProgressionOverride[],
  sets: readonly SetDraft[],
  axis: ProgressionAxis,
): ProgressionOverride[] {
  let next = [...overrides];
  for (const override of overridesDroppedByRewrite(overrides, sets, axis)) {
    next = upsertOverride(next, override.setIndex, override.cycleIndex, null, axis);
  }
  return next;
}

/**
 * Which COLUMNS an exercise carries manual values on — what a "clear everything" confirm has to be
 * able to name before it asks. Ladder percents are excluded for the same reason they survive every
 * rewrite. The `'percent'` member is the %1RM column, which names no measurement field.
 */
export function pinnedColumns(
  overrides: readonly ProgressionOverride[],
  sets: readonly SetDraft[],
): ProgressionAxisKind[] {
  const seen = new Set<ProgressionAxisKind>();
  for (const override of overrides) {
    if (isLadderPercentOverride(override, sets)) continue;
    for (const [field, key] of PINNABLE_COLUMNS) {
      if ((override[key] ?? null) !== null) seen.add(field);
    }
    if (override.prescribedPercent !== null) seen.add('percent');
  }
  return [...seen];
}

/** The override for one cell, or `undefined`. */
export function findOverride(
  overrides: readonly ProgressionOverride[],
  setIndex: number,
  cycleIndex: number,
): ProgressionOverride | undefined {
  return overrides.find(
    (override) => override.setIndex === setIndex && override.cycleIndex === cycleIndex,
  );
}

/**
 * The raw value an override pins on the active axis — that axis's own column.
 *
 * An override pins per COLUMN, so a cell the trainer corrected on the reps column reads as UNPINNED
 * on the weight column. That is what "I corrected the reps in cycle 3 and it froze the weight too"
 * was, and `withAxisPin` is the write half of the same fix.
 */
export function overrideValueOn(
  override: ProgressionOverride,
  axis: ProgressionAxis,
): number | null {
  return override[axis.setField] ?? null;
}

/**
 * Where a row's projection starts: the value at one lifespan position, from which every later
 * position advances one rule step per exposure. `{ offset: 0, value: base }` is the authoring-time
 * reading — the base column, projected forward.
 */
export interface ChainAnchor {
  readonly offset: number;
  readonly value: number | null;
}

/**
 * The statuses of an occurrence that FINISHED — the backend's `SlotStatuses.IsFinished`. Only a
 * finished session feeds the chain (`ProgressionReferenceRepository.GetPreviousInPhaseExecutionAsync`
 * reads `SessionLifecycle.Completed`); a missed, declined, cancelled or running one is read past.
 */
const FINISHED_STATUSES: ReadonlySet<string> = new Set(['Done', 'DoneLate']);

/**
 * Where a live fork's RECORD leaves one row of a `PreviousExecution` chain, or the authoring-time
 * anchor wherever the record has nothing to say.
 *
 * ── Why the base column stops being the origin once the client has trained ────────────────────
 * The rule resolves at workout start off the previous FINISHED in-phase session of the slot: per set
 * index, that session's logged actual plus the step when the backend judged it delivered, the same
 * actual when it judged it short, and the authored prescription where there is no finished session
 * or no actual on that set (`PreviousExecutionProgressionModule.ResolveAtStartAsync`, R3/R7). So the
 * first cycle after the record is not an estimate at all, it is already decided — and projecting it
 * from the base as if every lived cycle had been delivered drew 105 kg over a cycle the backend
 * prescribes at 100, beside a red cycle and under a trajectory that said 100. Every cycle after
 * that one keeps the authoring convention: one step per exposure, a client who delivers.
 *
 * The verdict is READ, as everywhere on this surface: `delivered` is the backend's own gate, and a
 * finished session carrying no flag (an older server) leaves the row on the authoring-time anchor
 * rather than this module guessing which way it went.
 *
 * Two places a backend detail is approximated rather than reproduced: the chain orders by
 * `FinishedAt` and this by cycle (they differ only for a session finished after a later one), and a
 * self-paced make-up, which the chain reads past, is not told apart on the wire.
 *
 * Rows the rewrite never touches keep the authoring anchor: a warm-up, a ladder member, a row priced
 * off the client's max, and every row on the %1RM axis, whose cells are fractions while the chain
 * writes kilograms.
 */
export function chainAnchorOf(
  exercise: Pick<ExerciseDraft, 'progressionRules' | 'measurementFields' | 'executions'>,
  set: SetDraft,
  setIndex: number,
  lifespan: CycleLifespan,
  axis: ProgressionAxis,
  base: number | null,
): ChainAnchor {
  const authoring: ChainAnchor = { offset: 0, value: base };
  const field = axis.field;
  if (field === null || axis.kind === 'percent' || !resolvesInSession(exercise, axis)) return authoring;
  if (!countsAsWork(set.setType) || derivesLoadInSession(set) || set.prescribedPercent !== null) {
    return authoring;
  }

  const cycles = lifespan.cycles;
  const record = (exercise.executions ?? []).filter((execution) =>
    cycles.includes(execution.cycleIndex),
  );
  if (record.length === 0) return authoring;

  // Any occurrence in the record makes its cycle lived, a missed one included — the same reading
  // the sheet's „TU JEST KLIENT" marker takes. The anchor sits on the first cycle after all of them.
  const lastLived = Math.max(...record.map((execution) => execution.cycleIndex));
  const offset = cycles.findIndex((cycle) => cycle > lastLived);
  if (offset <= 0) return authoring;

  const reference = record
    .filter((execution) => FINISHED_STATUSES.has(execution.status))
    .sort((a, b) => b.cycleIndex - a.cycleIndex)[0];
  // No finished session yet: the next start is a first exposure and prescribes what was authored.
  if (reference === undefined) return { offset, value: base };
  if (reference.delivered === null) return authoring;

  const actual = reference.sets.find((row) => row.setIndex === setIndex)?.actual[field];
  if (actual === undefined) return { offset, value: base };
  const value = reference.delivered
    ? projectSnapped(actual, 1, ruleFromExercise(exercise, axis), axis)
    : snapToAxis(actual, axis);
  return { offset, value };
}

/** The value at a lifespan position, projected from the row's anchor. */
export function projectFromAnchor(
  anchor: ChainAnchor,
  cycleOffset: number,
  base: number | null,
  rule: ProjectionRule,
  axis: ProgressionAxis,
): number | null {
  return cycleOffset >= anchor.offset
    ? projectSnapped(anchor.value, cycleOffset - anchor.offset, rule, axis)
    : projectSnapped(base, cycleOffset, rule, axis);
}

/**
 * Build the matrix for an exercise across its LIFESPAN — the cycles it is actually prescribed in,
 * read off the wire and resolved by `cycleLifespan`, never a span computed from the phase. The FIRST
 * entry is the authored base; later entries are the rule's projection unless a manual override pins
 * them. Cycles outside the lifespan — before the activation, past the retirement, or SAT OUT in the
 * middle of it — get no column at all, matching the backend, which materialises no rows for them and
 * refuses an override that names one.
 *
 * Warm-up rows are flat with no override path: the backend rejects one outright.
 */
export function buildMatrix(
  exercise: ExerciseDraft,
  lifespan: CycleLifespan,
  axis: ProgressionAxis,
): MatrixRow[] {
  const rule = ruleFromExercise(exercise, axis);
  const cycles = lifespan.cycles;

  return exercise.sets.map((set, index) => {
    const setIndex = index + 1;
    const isWarmup = set.setType === 'Warmup';
    // A descent's row is the "% of the top" ladder — the percent is what the row prescribes,
    // whatever axis the matrix is on, so its base is the fraction and its cells never carry a load.
    const isBackoffDescent = isBackoffSet(set);
    // On the %1RM axis EVERY cycle — the base one included — is asked of `prescribedPercentInCycle`:
    // the row may price itself in nothing and take the exercise's multiplier instead, so what cycle
    // one holds is an answer rather than a field read. A descent stays a field read on every axis,
    // because its fraction is of the TOP rather than of the max.
    const isPercentAxis = axis.kind === 'percent' && !isBackoffDescent;
    const percentIn = (offset: number): number | null =>
      prescribedPercentInCycle(exercise, set, offset);
    const base = isBackoffDescent
      ? set.prescribedPercent
      : isPercentAxis
        ? percentIn(0)
        : baseValueOf(set, axis);
    // On a live fork a dynamic row restarts from where the record leaves it — see `chainAnchorOf`.
    const anchor = chainAnchorOf(exercise, set, setIndex, lifespan, axis, base);

    const cells: MatrixCell[] = [];
    for (const [offset, cycleIndex] of cycles.entries()) {
      if (offset === 0) {
        cells.push({ setIndex, cycleIndex, cycleOffset: 0, valueRaw: base, kind: 'base' });
        continue;
      }
      if (isWarmup) {
        cells.push({ setIndex, cycleIndex, cycleOffset: offset, valueRaw: base, kind: 'warmupFlat' });
        continue;
      }
      const override = findOverride(exercise.progressionOverrides, setIndex, cycleIndex);
      if (isBackoffDescent) {
        // Percent-aware and axis-independent: only a pinned PERCENT overrides the cell, because an
        // override carrying an axis value on this row is one the backend refuses, so it cannot be
        // what the cell shows. An unpinned cell replicates the base — a descent has no rule of its
        // own; the top's progression is what moves it.
        const pinned = override?.prescribedPercent ?? null;
        cells.push(
          pinned === null
            ? { setIndex, cycleIndex, cycleOffset: offset, valueRaw: base, kind: 'computed' }
            : { setIndex, cycleIndex, cycleOffset: offset, valueRaw: pinned, kind: 'override' },
        );
        continue;
      }
      // Only a pin ON THIS COLUMN overrides the cell. An override pinning another column leaves this
      // one on the rule, which is the whole reason a pin is per field.
      const pinned = override === undefined ? null : overrideValueOn(override, axis);
      if (pinned !== null) {
        cells.push({ setIndex, cycleIndex, cycleOffset: offset, valueRaw: pinned, kind: 'override' });
        continue;
      }
      // The offset is the cell's position in the LIFESPAN — how many times the exercise has run
      // before this cycle — so a week it sits out advances the rule by nothing.
      const value = isPercentAxis
        ? percentIn(offset)
        : projectFromAnchor(anchor, offset, base, rule, axis);
      // A flat rule computes nothing, it copies. The cell says so rather than wearing the identity
      // of a projection, and it stays editable: pinning a value on an unruled column is ordinary
      // manual authoring.
      cells.push({
        setIndex,
        cycleIndex,
        cycleOffset: offset,
        valueRaw: value,
        kind: rule.kind === 'flat' ? 'flat' : 'computed',
      });
    }
    return { setIndex, isWarmup, isBackoffDescent, cells };
  });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The override store — pure list surgery. The store act applies the result; nothing here mutates.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** Every column a pin can sit on, and the override key it writes. Percent is asked separately. */
const PINNABLE_COLUMNS: readonly (readonly [
  MeasurementFieldKey,
  keyof Omit<ProgressionOverride, 'setIndex' | 'cycleIndex' | 'isManuallyOverridden' | 'prescribedTempo'>,
])[] = [
  ['weight', 'prescribedWeight'],
  ['reps', 'prescribedReps'],
  ['duration', 'prescribedDurationSeconds'],
  ['distance', 'prescribedDistanceMeters'],
  ['rounds', 'prescribedRounds'],
  ['speed', 'prescribedSpeed'],
  ['rpe', 'prescribedRpe'],
  ['rir', 'prescribedRir'],
];

/** An override pinning nothing at all — a cell with nothing left to say, which is dropped. */
function isEmptyOverride(override: ProgressionOverride): boolean {
  if (override.prescribedPercent !== null) return false;
  if ((override.prescribedRestTime ?? null) !== null) return false;
  if ((override.prescribedTempo ?? null) !== null) return false;
  return PINNABLE_COLUMNS.every(([, key]) => (override[key] ?? null) === null);
}

/**
 * One cell's pinned values with the active axis's column set to `valueRaw`, or cleared when it is
 * null. **Every OTHER column the cell already pins is left exactly where it was.**
 *
 * That is the fix this shape exists for. The write used to mint a fresh override carrying the axis's
 * value and four explicit nulls, so pinning a load in cycle three silently erased the repetitions
 * the trainer had pinned in the same cell — and the save, which is the sole source of overrides,
 * made the erasure real.
 */
function withAxisPin(
  existing: ProgressionOverride | undefined,
  setIndex: number,
  cycleIndex: number,
  valueRaw: number | null,
  axis: ProgressionAxis,
): ProgressionOverride {
  const base: ProgressionOverride = existing ?? {
    setIndex,
    cycleIndex,
    prescribedWeight: null,
    prescribedPercent: null,
    prescribedRpe: null,
    prescribedRir: null,
    isManuallyOverridden: true,
  };
  return { ...base, setIndex, cycleIndex, [axis.setField]: valueRaw };
}

/**
 * Pin a cell to `valueRaw` in the active unit, or clear THIS COLUMN'S pin back to the rule when it
 * is null. Pins the same cell carries on other columns survive, and the override is dropped only
 * once nothing is pinned on it at all.
 */
export function upsertOverride(
  overrides: readonly ProgressionOverride[],
  setIndex: number,
  cycleIndex: number,
  valueRaw: number | null,
  axis: ProgressionAxis,
): ProgressionOverride[] {
  const existing = findOverride(overrides, setIndex, cycleIndex);
  const without = overrides.filter(
    (override) => !(override.setIndex === setIndex && override.cycleIndex === cycleIndex),
  );
  const next = withAxisPin(existing, setIndex, cycleIndex, valueRaw, axis);
  return isEmptyOverride(next) ? without : [...without, next];
}

/** Drop one cell's pin on EVERY column, reverting it to the rule. */
export function clearOverride(
  overrides: readonly ProgressionOverride[],
  setIndex: number,
  cycleIndex: number,
): ProgressionOverride[] {
  return overrides.filter(
    (override) => !(override.setIndex === setIndex && override.cycleIndex === cycleIndex),
  );
}

/**
 * Commit a LATER cycle's "% of the top" on a descent. The result is always a PERCENT-ONLY override:
 *
 * · a blank clears the cell back to the base percent;
 * · a fraction equal to the base clears it too — the matrix already reads that way, so pinning it
 *   would ship a no-op cell the trainer would then have to un-pin;
 * · a non-positive fraction is not a prescription the backend accepts, so the grid is left exactly
 *   as it was rather than clearing what the trainer already set;
 * · anything else pins `min(fraction, 1)` — above the top's own load is not a descent.
 */
export function commitBackoffPercent(
  overrides: readonly ProgressionOverride[],
  basePercent: number | null,
  setIndex: number,
  cycleIndex: number,
  fraction: number | null,
): ProgressionOverride[] {
  if (fraction === null) return clearOverride(overrides, setIndex, cycleIndex);
  if (fraction <= 0) return [...overrides];
  const clamped = Math.min(1, fraction);
  if (basePercent !== null && Math.abs(clamped - basePercent) < 1e-9) {
    return clearOverride(overrides, setIndex, cycleIndex);
  }
  return upsertOverride(overrides, setIndex, cycleIndex, clamped, PERCENT_AXIS);
}

/** Whether any cell is pinned — drives the legend's manual row and the "recompute" affordance. */
export function hasOverrides(overrides: readonly ProgressionOverride[]): boolean {
  return overrides.length > 0;
}
